import { FormikErrors } from 'formik';
import * as Yup from 'yup';
import type { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import type {
  CallSummaryPayload,
  CallTranscriptPayload,
} from '@rsos/reduxDataStore/src/features/eventShare/types';
import { isValidEmail } from '@rsos/utils/validators';
import { LATEST_SUMMARY_INDEX } from '../../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice/transcriptionAdapter';
import type { TranscriptionStateCall } from '../../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice/transcriptionSlice.types';
import { type Checkbox } from '../../constants/ShareEvent.types';
import dataSources, { DATA_SOURCE_IDS } from '../../constants/dataSources';
import {
  MAP_DISPLAY_CONFIG,
  SHARE_EXPIRY_SECONDS,
} from '../../constants/shareEventDefaults';
import {
  FIELD_DIRECT_RECIPIENTS_SHARE,
  FIELD_IAR_SHARE,
  type FieldSharingMethod,
} from '../../constants/shareMethods';
import { type MultiInputTag } from './ShareEventRecipients';

/** Data-source ids whose live data is pulled from the transcription slice.
 * Sourced from DATA_SOURCE_IDS so they can't drift from the checkbox ids. */
export const CALL_SUMMARY_SOURCE_ID = DATA_SOURCE_IDS.CALL_SUMMARY;
export const CALL_TRANSCRIPT_SOURCE_ID = DATA_SOURCE_IDS.CALL_TRANSCRIPT;

export interface IarSelectedMember {
  agency_id: number;
  member_id: number;
  name?: string;
  subscriber_name?: string;
}

export interface FieldFormValues {
  recipients: MultiInputTag[];
  sharingMethod: FieldSharingMethod;
  dataSources: Checkbox[];
  notes: string;
  iarSelectedMembers: IarSelectedMember[];
}

/**
 * The Field Share modal is a small wizard. Steps are identified by string keys
 * (not positional numbers) so the IaR-only `IAR_SELECT` step can sit between
 * `RECIPIENTS` and `DATA_SOURCES` without renumbering the rest. Order is defined
 * per sharing method by `getStepSequence`, not by the value of these keys.
 */
export const SHARE_STEP = {
  RECIPIENTS: 'recipients',
  IAR_SELECT: 'iar-select',
  DATA_SOURCES: 'data-sources',
  NOTES: 'notes',
  SHARE_LINK: 'share-link',
} as const;

export type StepId = (typeof SHARE_STEP)[keyof typeof SHARE_STEP];

export const getStepSequence = (method: FieldSharingMethod): StepId[] =>
  method === FIELD_IAR_SHARE
    ? [
        SHARE_STEP.RECIPIENTS,
        SHARE_STEP.IAR_SELECT,
        SHARE_STEP.DATA_SOURCES,
        SHARE_STEP.NOTES,
      ]
    : [
        SHARE_STEP.RECIPIENTS,
        SHARE_STEP.DATA_SOURCES,
        SHARE_STEP.NOTES,
        SHARE_STEP.SHARE_LINK,
      ];

export const getNextStep = (
  current: StepId,
  method: FieldSharingMethod,
): StepId | null => {
  const seq = getStepSequence(method);
  const idx = seq.indexOf(current);
  return idx >= 0 && idx < seq.length - 1 ? seq[idx + 1] : null;
};

export const getPrevStep = (
  current: StepId,
  method: FieldSharingMethod,
): StepId | null => {
  const seq = getStepSequence(method);
  const idx = seq.indexOf(current);
  return idx > 0 ? seq[idx - 1] : null;
};

/** Create the validation schema based on the current step and sharing method */
export const createValidationSchema = (
  currentStep: StepId,
  sharingMethod: FieldSharingMethod,
) => {
  // Data sources are only required once the wizard has reached the data-sources
  // step. Order varies by sharing method, so compare positions in the sequence.
  const sequence = getStepSequence(sharingMethod);
  const hasReachedDataSources =
    sequence.indexOf(currentStep) >= sequence.indexOf(SHARE_STEP.DATA_SOURCES);

  const baseValidation = {
    sharingMethod: Yup.string().required(),
    dataSources: Yup.array().test(
      'datasources-required',
      'Select at least one data source',
      function (value: Checkbox[] | undefined) {
        if (hasReachedDataSources) {
          return Boolean(
            value && value.some((source: Checkbox) => source.isChecked),
          );
        }
        return true;
      },
    ),
    sessions: Yup.array(),
    notes: Yup.string(),
  };

  // Different validation based on sharing method
  const recipientsValidation =
    sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE
      ? Yup.array()
          .min(1, 'At least one recipient is required')
          .test(
            'recipients-valid',
            'Invalid email format',
            function (value: MultiInputTag[] | undefined) {
              if (value && value.length > 0) {
                return value.every(
                  (recipient: MultiInputTag) => recipient.isValid,
                );
              }
              return true;
            },
          )
          .test(
            'recipients-limit',
            'Limit reached: Total recipients cannot exceed 10.',
            function (value: MultiInputTag[] | undefined) {
              return !(value && value.length > 10);
            },
          )
      : Yup.array();

  return Yup.object().shape({
    ...baseValidation,
    recipients: recipientsValidation,
  });
};

export const createInitialValues = (
  sharingMethod: FieldSharingMethod,
): FieldFormValues => ({
  recipients: [],
  sharingMethod,
  dataSources: dataSources.map(source => ({ ...source, isChecked: false })), // TODO: update to true if checked by default
  notes: '',
  iarSelectedMembers: [],
});

// Step validation logic
export const isStepValid = (
  step: StepId,
  values: FieldFormValues,
  errors: FormikErrors<FieldFormValues>,
  sharingMethod: FieldSharingMethod,
): boolean => {
  switch (step) {
    case SHARE_STEP.RECIPIENTS:
      // Check for Formik errors first
      if (errors.recipients || errors.sharingMethod) {
        return false;
      }

      // For direct recipients, also check that we have valid recipients
      if (sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE) {
        return (
          values.recipients.length > 0 &&
          values.recipients.every(recipient => recipient.isValid)
        );
      }

      // For share-link and IaR, no recipients needed on the first step
      return true;
    case SHARE_STEP.IAR_SELECT:
      return values.iarSelectedMembers.length > 0;
    case SHARE_STEP.DATA_SOURCES:
      // Check for Formik errors first
      if (errors.dataSources) {
        return false;
      }

      // Also check that at least one data source is selected
      return values.dataSources.some(source => source.isChecked);
    case SHARE_STEP.NOTES:
      return true; // Notes are optional
    case SHARE_STEP.SHARE_LINK:
      return true; // Share link display step
    default:
      return false;
  }
};

const hasCoordinates = (
  loc: unknown,
): loc is { latitude: number; longitude: number } => {
  const obj = loc as Record<string, unknown>;
  return obj?.latitude != null && obj?.longitude != null;
};

const buildCallContext = (
  selectedCall: EmergencyCall | undefined,
  checkedSourceIds: Set<string>,
) => {
  if (!selectedCall) return {};

  const location = selectedCall.core?.locations?.[0];

  return {
    // Location-derived fields back the always-on map pin, so they are gated by
    // data presence rather than a checkbox.
    ...(location?.caller_id ? { call: { ani: location.caller_id } } : {}),
    ...(hasCoordinates(location)
      ? {
          spatial_layer: {
            latitude: location.latitude,
            longitude: location.longitude,
          },
        }
      : {}),
    // Caller Data is only shared when its source is checked — unchecking it
    // must keep additional_data out of the payload.
    ...(checkedSourceIds.has(DATA_SOURCE_IDS.ADDITIONAL_DATA) &&
    selectedCall.additionalData
      ? { additional_data: selectedCall.additionalData }
      : {}),
  };
};

/**
 * Maps the latest call summary from the transcription slice to the shared
 * payload shape. Returns undefined when no summary has been generated yet so
 * the field is omitted rather than sent empty.
 */
export const buildCallSummaryPayload = (
  transcription?: TranscriptionStateCall,
): CallSummaryPayload | undefined => {
  const latest = transcription?.summaries?.[LATEST_SUMMARY_INDEX];
  if (!latest?.summary) return undefined;
  return {
    summary: latest.summary,
    keywords: latest.keywords ?? [],
    is_final: latest.is_final,
  };
};

/**
 * Maps the call transcript tokens + audio status from the transcription slice
 * to the shared payload shape. Returns undefined when there are no tokens yet.
 */
export const buildCallTranscriptPayload = (
  transcription?: TranscriptionStateCall,
): CallTranscriptPayload | undefined => {
  const payload = transcription?.payload;
  if (!payload || !payload.tokens?.length) return undefined;
  return {
    tokens: payload.tokens.map(token => ({
      token_id: token.token_id,
      speaker_display_name: token.speaker_display_name,
      transcript: token.transcript,
      start_time: token.start_time,
      end_time: token.end_time,
    })),
    call_information: payload.call_information,
  };
};

export const createSubmissionPayload = (
  values: FieldFormValues,
  sharingMethod: FieldSharingMethod,
  uri: string,
  organization: string,
  selectedCall?: EmergencyCall,
  transcription?: TranscriptionStateCall,
) => {
  const isIar = sharingMethod === FIELD_IAR_SHARE;
  const isDirectRecipients = sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE;

  const checkedSources = values.dataSources.filter(source => source.isChecked);
  const checkedSourceIds = new Set(checkedSources.map(source => source.id));

  const callContext = buildCallContext(selectedCall, checkedSourceIds);

  const callSummary = checkedSourceIds.has(CALL_SUMMARY_SOURCE_ID)
    ? buildCallSummaryPayload(transcription)
    : undefined;
  const callTranscript = checkedSourceIds.has(CALL_TRANSCRIPT_SOURCE_ID)
    ? buildCallTranscriptPayload(transcription)
    : undefined;

  // Every Field Share sends the map entry in display_config so the Connect
  // Share view always shows the location pin alongside any selected data
  // sources. (IaR additionally attaches a `members` array — see below.)
  const displayConfig = [
    ...checkedSources.map(source => ({ name: source.id, meta: {} })),
    ...MAP_DISPLAY_CONFIG,
  ];

  // Only direct-recipients shares carry recipient emails; link and IaR shares
  // leave them empty. Recipients are email-only — phone/SMS delivery was removed.
  const emails: string[] = [];
  if (isDirectRecipients) {
    values.recipients.forEach(recipient => {
      if (isValidEmail(recipient.value)) {
        emails.push(recipient.value);
      }
    });
  }

  return {
    uri,
    organization,
    ...(isIar ? { expiry: SHARE_EXPIRY_SECONDS } : {}),
    recipients: {
      emails,
      phone_numbers: [],
      organization_contacts: [],
      contact_groups: [],
    },
    ...(isIar ? { members: values.iarSelectedMembers } : {}),
    display_config: displayConfig,
    additional_comments: values.notes ? [values.notes] : [],
    ...callContext,
    ...(callSummary ? { call_summary: callSummary } : {}),
    ...(callTranscript ? { call_transcript: callTranscript } : {}),
  };
};
